import {
  ComponentProps,
  MouseEvent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DroppableProvidedProps,
} from 'react-beautiful-dnd';
import styled, { css } from 'styled-components';
import { ControlledMenu, MenuItem } from '@szhsin/react-menu';

import {
  paradigmLightBlue,
  tangaroa,
} from '@paradigm/design-system/src/colors';

import BoldAddIcon from '@paradigm/design-system/src/assets/BoldAddIcon';
import BoldCross from '@paradigm/design-system/src/assets/BoldCross';
import { AnchorPoint } from '#/components/context-menu';
import { useHorizontalScroll } from '#/hooks/useHorizontalScroll';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';

import { LongText } from '../components/LongText';
import RenameTabModal from './RenameTabModal';
import { TabsSearch } from './TabsSearch';
import type { Tab as TabState } from '@paradigm/features/src/unified-rfqs/ui/rfq-filters/tabs-context';

export interface Tab {
  id: string;
  name: string;
  index: number;
}

export const MAIN_TAB_ID = 'main';
const TAB_WIDTH = 108;

interface Props {
  readonly deleteTab: (id: string) => void;
  readonly viewTab: (id: string) => void;
  readonly addTab: (id: string, name: string, setActive: boolean) => void;
  readonly reorderTab: (srcIndex: number, destIndex: number) => void;
  readonly tabs: readonly TabState[];
  readonly activeTab: Tab;
  readonly generateId?: () => string;
  readonly deleteAllTabs: () => void;
}

export default function Tabs({
  deleteTab,
  viewTab,
  addTab,
  tabs,
  activeTab,
  reorderTab,
  deleteAllTabs,
  generateId = () => Math.random().toString(),
}: Props) {
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);

  const onAddTab = () => {
    const id = generateId();
    addTab(id, `Untitled ${tabs.length}`, true);
  };
  const onViewTab = useCallback(
    (id: string) => {
      viewTab(id);
    },
    [viewTab],
  );

  const onDragEnd: ComponentProps<typeof DragDropContext>['onDragEnd'] = ({
    source,
    destination,
  }) => {
    if (!destination) return;
    reorderTab(source.index, destination.index);
  };

  const toggleRenameModalState = useCallback(() => {
    setIsRenameModalOpen(!isRenameModalOpen);
  }, [isRenameModalOpen]);

  const onRenameTab = useCallback(
    (id: string) => {
      if (isRenameModalOpen && activeTab.id === id) return;
      onViewTab(id);
      toggleRenameModalState();
    },
    [activeTab.id, isRenameModalOpen, onViewTab, toggleRenameModalState],
  );

  // If user deletes current tab, change to main
  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTab.id)) {
      onViewTab(MAIN_TAB_ID);
    }
  }, [tabs, activeTab.id, onViewTab]);

  const searchData = useMemo(
    () =>
      tabs.map((tab) => ({
        ...tab,
        key: tab.id,
        value: tab.name,
        description: tab.name,
        isRemovable: tab.id !== MAIN_TAB_ID,
      })),
    [tabs],
  );

  return (
    <Container>
      <div css="display: flex;">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable
            droppableId="unified-markets-tabs"
            direction="horizontal"
            ignoreContainerClipping
          >
            {(providedDrop) => (
              <TabsContainerWrapper
                numTabs={tabs.length}
                callbackRef={providedDrop.innerRef}
                {...providedDrop.droppableProps}
              >
                {tabs.map((tab, index) =>
                  tab.id === MAIN_TAB_ID ? (
                    <MainTab
                      key={tab.id}
                      aria-label="view tab main"
                      data-testid={`tab-${MAIN_TAB_ID}`}
                      currentTab={activeTab.id}
                      onClick={() => onViewTab(MAIN_TAB_ID)}
                    />
                  ) : (
                    <div key={tab.id}>
                      <Tab
                        index={index}
                        tab={tab}
                        currentTab={activeTab.id}
                        viewTab={onViewTab}
                        deleteTab={deleteTab}
                        renameTab={onRenameTab}
                      />
                      <RenameTabModal
                        tab={tab}
                        isOpen={isRenameModalOpen && activeTab.id === tab.id}
                        close={toggleRenameModalState}
                      />
                    </div>
                  ),
                )}
                {providedDrop.placeholder}
              </TabsContainerWrapper>
            )}
          </Droppable>
        </DragDropContext>
        <AddTabIcon data-testid="add-new-tab" onClick={onAddTab} />
      </div>
      <TabsSearch
        data={searchData}
        onSelect={(tab) => onViewTab(tab.id)}
        onRemove={(tab) => deleteTab(tab.id)}
        onRemoveAll={deleteAllTabs}
        maxWidth="332px"
        minWidth="80px"
      />
    </Container>
  );
}

interface TabsContainerWrapperProps extends DroppableProvidedProps {
  callbackRef: (e: HTMLElement | null) => void;
  numTabs: number;
}

const TabsContainerWrapper: React.FC<
  PropsWithChildren<TabsContainerWrapperProps>
> = ({ children, callbackRef, ...props }) => {
  const ref = useHorizontalScroll<HTMLDivElement>(callbackRef);

  return (
    <TabsContainer {...props} ref={ref}>
      {children}
    </TabsContainer>
  );
};

interface MainTabProps extends ComponentProps<typeof TabContainer> {
  readonly currentTab: string;
}

function MainTab({ currentTab, ...props }: MainTabProps) {
  return (
    <TabContainer {...props} isActive={currentTab === MAIN_TAB_ID}>
      Main
    </TabContainer>
  );
}

interface TabProps {
  readonly currentTab: string;
  readonly tab: TabState;
  readonly index: number;
  readonly deleteTab: (id: string) => void;
  readonly viewTab: (id: string) => void;
  readonly renameTab: (id: string) => void;
}

function Tab({
  currentTab,
  tab,
  deleteTab,
  viewTab,
  renameTab,
  index,
  ...props
}: TabProps) {
  const [anchorPoint, setAnchorPoint] = useState<AnchorPoint | undefined>();
  const handleDelete = (event: MouseEvent<SVGSVGElement>) => {
    event.stopPropagation();
    deleteTab(tab.id);
  };

  return (
    <Draggable draggableId={tab.id} index={index}>
      {(providedDrag) => (
        <div
          ref={providedDrag.innerRef}
          {...providedDrag.draggableProps}
          {...providedDrag.dragHandleProps}
        >
          <TabContainer
            {...props}
            data-testid={`tab-${tab.id}`}
            onContextMenu={(event) => {
              event.stopPropagation();
              event.preventDefault();
              setAnchorPoint({ x: event.clientX, y: event.clientY });
            }}
            onClick={() => viewTab(tab.id)}
            isActive={currentTab === tab.id}
          >
            <LongText tooltip={tab.name}>{tab.name}</LongText>
            <CloseTabIcon
              data-testid={`delete-tab-${tab.id}`}
              onClick={handleDelete}
            />
          </TabContainer>
          <TabMenu
            close={() => setAnchorPoint(undefined)}
            anchorPoint={anchorPoint}
            renameTab={() => renameTab(tab.id)}
          />
        </div>
      )}
    </Draggable>
  );
}

interface TabMenuProps {
  readonly close: () => void;
  readonly anchorPoint: AnchorPoint | undefined;
  readonly renameTab: () => void;
}

function TabMenu({ close, anchorPoint, renameTab }: TabMenuProps) {
  return (
    <ControlledMenu
      css="font-weight: normal; position: absolute;"
      anchorPoint={anchorPoint}
      state={anchorPoint != null ? 'opening' : 'closing'}
      onClose={close}
    >
      {anchorPoint != null && <MenuItem onClick={renameTab}>Rename</MenuItem>}
    </ControlledMenu>
  );
}

const CloseTabIcon = styled(BoldCross)`
  transition: color ease 0.1s;
  cursor: pointer;

  &:hover {
    color: white;
  }
`;

const AddTabIcon = styled.button.attrs({
  children: <BoldAddIcon width={10} height={10} />,
})`
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: transparent;
  padding: 0;
  cursor: pointer;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 6px;

  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }

  & > svg {
    color: ${paradigmLightBlue};
  }
`;

const TabContainer = styled.div.attrs({ role: 'tab' })<{ isActive: boolean }>`
  position: relative;
  user-select: none;
  width: ${TAB_WIDTH}px;
  min-width: ${TAB_WIDTH}px;
  height: 25px;
  border-radius: 3px 3px 0 0;
  padding: 0 8px;
  margin: 0 4px;
  display: grid;
  grid-template-columns: auto 8px;
  gap: 4px;
  align-items: center;
  font-size: 13px;
  cursor: pointer;
  font-weight: bold;
  ${(props) =>
    props.isActive
      ? css`
          color: ${paradigmLightBlue};
          background-color: #051524;
        `
      : css`
          color: #8492a3;
          background-color: #092647;
        `}
`;

const Container = styled.div`
  position: relative;
  height: 34px;
  width: 100%;
  padding: 0 16px;
  display: flex;
  justify-content: space-between;
  background: ${tangaroa};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const TabsContainer = styled.div<{ numTabs: number }>`
  display: inline-flex;
  flex: 1;
  flex-wrap: 1;
  padding-right: 2px;
  box-sizing: content-box;
  width: ${(props) => props.numTabs * (TAB_WIDTH + 8)}px;
  max-width: calc(100vw - 50px - ${TAB_WIDTH}px - 160px);
  overflow: auto;
  align-items: flex-end;

  &::-webkit-scrollbar {
    display: none;
  }
`;
